import { get } from 'lodash';
import { useCallback } from 'react';

import { Card } from '@repo/ui';
import { cn, dateUtil, numberUtil } from '@xtnd/web/shared/utils';

import { DAYS_PAST_DUE } from '../../constants';
import { ArAppealStatus } from '../common';
import { AppealStatus } from '@repo/types';

import type { Invoice } from '../../types/invoice';
import { ANIMATION_DELAY_200MS_STYLE } from '../../utils/attorney-response';

export interface InvoiceDetailsProps {
  invoice: Invoice;
  totalAmount: number;
  amountDue: number;
  daysPastDue: number;
  className?: string;
}

export const InvoiceDetails = ({
  invoice,
  totalAmount,
  amountDue,
  daysPastDue,
  className,
}: InvoiceDetailsProps) => {
  const renderAgreedToPay = useCallback(() => {
    const collectedAmount = get(invoice, 'appeal.collectedAmount', 0);
    const excludeFromAutoWriteOff = get(invoice, 'excludeFromAutoWriteOff', false);
    const status = get(invoice, 'appealStatus', AppealStatus.PRE_APPEAL);

    if ((collectedAmount > 0 && status !== AppealStatus.NO_APPEAL) || excludeFromAutoWriteOff) {
      return (
        <div className="flex flex-col text-sm text-xtnd-dark dark:text-xtnd-light">
          <span>Agreed to Pay</span>
          <strong className="text-base">{numberUtil.formatCurrency(collectedAmount)}</strong>
        </div>
      );
    }
    return null;
  }, [invoice]);

  return (
    <Card
      className={cn('opacity-0 animate-fade-in-up duration-200', className)}
      style={ANIMATION_DELAY_200MS_STYLE}
    >
      {/* Matter Information */}
      <div className={'flex items-center gap-4 mb-8'}>
        <div>
          <span className="text-xl">{invoice.matterDisplayNumber || invoice.matterNumber}</span>
          <br />
          <span>{get(invoice, 'clientName', 'N/A')}</span>
        </div>
      </div>

      {/* Financial and Aging Information */}
      <div className="grid grid-cols-2 gap-6">
        {/* Left Column - Financial Details */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col text-sm text-xtnd-dark dark:text-xtnd-light">
            <span>Amount</span>
            <strong className="text-base">{numberUtil.formatCurrency(totalAmount)}</strong>
          </div>
          <div className="flex flex-col text-sm text-xtnd-dark dark:text-xtnd-light">
            <span>Paid</span>
            <strong className="text-base">{numberUtil.formatCurrency(invoice.paid ?? 0)}</strong>
          </div>
          <div className="flex flex-col text-sm text-xtnd-dark dark:text-xtnd-light">
            <span>Credits Issued</span>
            <strong className="text-base">
              {numberUtil.formatCurrency(invoice.creditsIssued ?? 0)}
            </strong>
          </div>
          <div className="flex flex-col text-sm text-xtnd-dark dark:text-xtnd-light">
            <span>Amount Due</span>
            <strong className="text-base">{numberUtil.formatCurrency(amountDue)}</strong>
          </div>
          {renderAgreedToPay()}
        </div>

        {/* Right Column - Aging and Contact Information */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col text-sm text-xtnd-dark dark:text-xtnd-light">
            <span className="mb-1">Oldest Days Past Due</span>
            <strong
              className={`inline-block py-1 px-2 w-fit rounded-full text-xtnd-dark dark:text-xtnd-light ${
                daysPastDue > DAYS_PAST_DUE
                  ? 'bg-[#ef4444] text-xtnd-white'
                  : 'dark:bg-xtnd-dark bg-xtnd-light'
              }`}
            >
              {daysPastDue} days
            </strong>
          </div>
          <div className="flex flex-col text-sm text-xtnd-dark dark:text-xtnd-light">
            <span className="mb-1">Appeal Status</span>
            <div className="inline-block w-fit">
              <ArAppealStatus status={invoice.appealStatus || AppealStatus.PRE_APPEAL} />
            </div>
          </div>
          <div className="flex flex-col text-sm text-xtnd-dark dark:text-xtnd-light">
            <span className="mb-1">Deadline</span>
            {invoice.appeal?.attyDeadline ? (
              <div className="inline-block w-fit font-bold">
                {dateUtil.formatDate(invoice.appeal.attyDeadline, dateUtil.DateFormat.US)}
              </div>
            ) : (
              <div className="inline-block w-fit font-bold">-</div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};
